import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { services } from "../data/services";
import { useLang } from "../i18n";
import { useEffect, useState } from "react";
import { track, loadPublicConfig, formatPhone } from "../lib/analytics";
import { mediaSrc } from "../lib/media";

export const CTABanner = ({
  title = "If your business needs a better system, let’s talk.",
  sub = "The first meeting is about identifying the constraint on your next stage of growth and deciding whether YUXEL is the right partner.",
  testid = "cta-banner",
}) => {
  const { t, href } = useLang();
  return (
  <section
    className="yuxel-cta-banner relative overflow-hidden py-28 sm:py-36"
    style={{ background: "#F7F5F2", color: "#0B0B0B" }}
  >
    <div className="spotlight-top pointer-events-none absolute inset-0" aria-hidden="true" />
    <div className="relative mx-auto max-w-4xl px-5 text-center sm:px-8">
      <p
        className="mb-6 font-mono text-xs uppercase tracking-[0.3em]"
        style={{ color: "#6B625C", opacity: 1 }}
      >
        {t("Strategic meeting")}
      </p>
      <h2
        className="text-4xl font-bold leading-[1.04] sm:text-5xl lg:text-6xl"
        style={{ color: "#0B0B0B", opacity: 1, fontFamily: "Archivo, Manrope, Arial, sans-serif" }}
      >
        {t(title)}
      </h2>
      <p
        className="mx-auto mt-6 max-w-xl text-base leading-relaxed sm:text-lg"
        style={{ color: "#5F5853", opacity: 1 }}
      >
        {t(sub)}
      </p>
      <Link
        to={href("/contact")}
        data-testid={`${testid}-button`}
        onClick={() => track("cta_click", { cta_id: testid, cta_location: "banner" })}
        className="group mt-10 inline-flex items-center gap-2 px-8 py-4 font-semibold transition-all duration-300"
        style={{ background: "#4B1020", color: "#F7F5F2", borderRadius: "999px" }}
      >
        {t("Schedule a strategic meeting")}
        <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </Link>
    </div>
  </section>
  );
};

export default function Footer() {
  const { t, href, ov } = useLang();
  const [phone, setPhone] = useState(null);
  useEffect(() => {
    loadPublicConfig().then((c) => c.whatsapp_number && setPhone(c.whatsapp_number));
  }, []);
  return (
    <footer className="border-t border-white/10 bg-panel">
      <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-20">
        <div className="grid grid-cols-2 gap-10 md:grid-cols-12">
          <div className="col-span-2 md:col-span-5">
            {ov("image:brand_logo", "/brand/yuxel-logo-full.png") ? (
              <img src={mediaSrc(ov("image:brand_logo", "/brand/yuxel-logo-full.png"))} alt="YUXEL" className="h-24 w-auto max-w-[240px] object-contain object-left" />
            ) : (
              <p className="font-display text-2xl font-bold tracking-[0.35em] text-white">YUXEL</p>
            )}
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-mist">
              {t("Business Transformation Company. Strategy, brand, marketing and sales systems for medium and large businesses.")}
            </p>
          </div>
          <div className="md:col-span-3">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold">{t("Services")}</p>
            <ul className="mt-5 space-y-2.5">
              {services.slice(0, 5).map((s) => (
                <li key={s.slug}>
                  <Link
                    to={href(`/services/${s.slug}`)}
                    data-testid={`footer-service-link-${s.slug}`}
                    className="text-sm text-white/70 transition-colors duration-300 hover:text-gold"
                  >
                    {t(s.name)}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to={href("/services")}
                  data-testid="footer-service-link-all"
                  className="text-sm font-medium text-white/70 transition-colors duration-300 hover:text-gold"
                >
                  {t("All services →")}
                </Link>
              </li>
            </ul>
          </div>
          <div className="md:col-span-2">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold">{t("Company")}</p>
            <ul className="mt-5 space-y-2.5">
              {[
                { to: "/work", l: "Projects" },
                { to: "/insights", l: "YUXEL Intelligence" },
                { to: "/about", l: "About YUXEL" },
                { to: "/contact", l: "Contact" },
              ].map((i) => (
                <li key={i.to}>
                  <Link
                    to={href(i.to)}
                    data-testid={`footer-link-${i.l.toLowerCase()}`}
                    className="text-sm text-white/70 transition-colors duration-300 hover:text-gold"
                  >
                    {t(i.l)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-2">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold">{t("Sectors")}</p>
            <ul className="mt-5 space-y-2.5 text-sm text-white/70">
              <li><Link to={href("/sectors/real-estate-marketing")} data-testid="footer-sector-real-estate" className="transition-colors hover:text-gold">{t("Real Estate Marketing")}</Link></li>
              <li><span>{t("Construction")}</span></li>
              <li><Link to={href("/sectors/horeca-marketing")} data-testid="footer-sector-horeca" className="transition-colors hover:text-gold">{t("HoReCa Marketing")}</Link></li>
              <li><span>{t("Entertainment")}</span></li>
            </ul>
          </div>
        </div>
        <div className="mt-16 flex flex-col items-start justify-between gap-4 border-t border-white/10 pt-8 sm:flex-row sm:items-center">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-6">
            <p className="text-xs text-mist">{t("© 2026 YUXEL")}</p>
            {phone && (
              <a
                href={`tel:+${phone}`}
                data-testid="footer-phone-link"
                onClick={() => track("phone_click", { phone })}
                className="text-xs text-white/70 transition-colors hover:text-gold"
              >
                {formatPhone(phone)}
              </a>
            )}
          </div>
          
        </div>
      </div>
    </footer>
  );
}
